/**
 * useAppVersionCheck
 * =============================================================================
 * Detecta que uma versão nova do site foi publicada enquanto a aba estava
 * aberta, para oferecer "recarregar" em vez de deixar a pessoa trabalhando
 * horas num build velho (que costuma dar erro estranho quando a API muda).
 *
 * COMO FUNCIONA
 *   O build do CRA gera /asset-manifest.json com os nomes dos bundles, e cada
 *   nome tem um hash do conteúdo. Se o manifest mudou, saiu build novo.
 *   Não precisa de rota nova no backend nem de número de versão manual.
 *
 * CUIDADOS
 *   - cache: 'no-store' senão o browser devolve o manifest antigo e nunca acusa.
 *   - Só checa com a aba visível: aba em segundo plano não precisa saber.
 *   - Qualquer falha (offline, deploy no meio) é ignorada em silêncio — isto é
 *     um aviso de conveniência, não pode virar erro na tela de ninguém.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const MANIFEST_URL = `${process.env.PUBLIC_URL || ''}/asset-manifest.json`;
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min

const fetchManifestSignature = async () => {
  const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`manifest HTTP ${res.status}`);
  const json = await res.json();
  // entrypoints é a lista de bundles carregados no index.html — muda a cada build
  return JSON.stringify(json.entrypoints || json.files || json);
};

export default function useAppVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef(null);

  const check = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    try {
      const signature = await fetchManifestSignature();
      if (baselineRef.current === null) {
        baselineRef.current = signature;   // primeira leitura = versão em uso
        return;
      }
      if (signature !== baselineRef.current) setUpdateAvailable(true);
    } catch {
      // silencioso de propósito: sem internet ou deploy em andamento
    }
  }, []);

  useEffect(() => {
    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  const reloadForUpdate = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reloadForUpdate };
}
